import { cascadeDescendants, getDb, serializeTaskMoves } from "@totodo/operations"
import type { DbObject } from "@totodo/operations"

/**
 * Save the quick-add draft into its container: write `updates` (the new parentPath, realm,
 * index, status) and take its descendants along, as every other move does (moveTask's
 * cascadeDescendants). The checklist added in quick-add lives under `${draft.parentPath}/${id}`;
 * left there, the saved task would show an empty checklist and the rows would stay in the
 * draft's realm. The rows go first, in the same transaction.
 *
 * Like every other move of a task, it runs under serializeTaskMoves: a checklist add still in
 * flight when Save is pressed reads the draft's path under the same queue, so its row lands either
 * before the save (and is cascaded) or after it (under the saved path) — never under the path the
 * save has just left. The path to cascade from is read there too, not taken from `draft`.
 */
export async function moveQuickAddDraft(
  draft: DbObject,
  updates: Partial<DbObject> & { parentPath: string; realmId: string },
): Promise<void> {
  const db = getDb()
  const id = draft.id as string
  await serializeTaskMoves([id], async () => {
    const from = (await db.totodo.get(id))?.parentPath ?? draft.parentPath
    await db.transaction("rw", db.totodo, async (tx) => {
      await cascadeDescendants(id, from, updates.parentPath, updates.realmId, tx)
      await db.totodo.update(id, updates)
    })
  })
}
